import { FC, Fragment, useLayoutEffect, useRef } from 'react';
import RjsfForm, { IChangeEvent } from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { FlowFormData, FlowMappingsConfig } from './types';

type RjsfFormInstance = InstanceType<typeof RjsfForm>;

/** Merges API uiSchema with app-only presentation for WhatsApp flow mapping fields. */
export const getMappingsUiSchema = (mappingsConfig: FlowMappingsConfig): Record<string, unknown> => {
  if (!mappingsConfig) {
    return {};
  }
  const mappingsUiSchema = (mappingsConfig.uiSchema ?? {}) as Record<string, unknown>;
  const userDataAddressUiSchema = (mappingsUiSchema['userDataAddress'] ?? {}) as Record<string, unknown>;
  const userDataIdentifierUiSchema = (mappingsUiSchema['userDataIdentifier'] ?? {}) as Record<string, unknown>;

  return {
    ...mappingsUiSchema,
    ['userDataAddress']: {
      'ui:classNames': 'hide-control-label-checkbox',
      ...userDataAddressUiSchema,
    },
    ['userDataIdentifier']: {
      'ui:classNames': 'hide-control-label-checkbox',
      ...userDataIdentifierUiSchema,
    },
  };
};

export type FlowMappingsFormProps = {
  config: NonNullable<FlowMappingsConfig>;
  formData: FlowFormData;
  onFormDataChange: (data: FlowFormData) => void;
  onValidationChange?: (isValid: boolean) => void;
};

/**
 * Server-driven RJSF surface for connector flow **mappings** (data mapping step).
 * Same RJSF baseline props as MultiStepFlowForm in MultiStepFlowFormv2.tsx (params step forms).
 */
export const FlowMappingsForm: FC<FlowMappingsFormProps> = ({ config, formData, onFormDataChange, onValidationChange }) => {
  const formRef = useRef<RjsfFormInstance | null>(null);
  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useLayoutEffect(() => {
    const isValid = formRef.current ? formRef.current.validateForm() : false;
    onValidationChangeRef.current?.(isValid);
  }, [config.schema, config.uiSchema]);

  return (
    <div className='whatsapp-flow-rjsf rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
      <RjsfForm
        ref={formRef}
        className='rjsf'
        tagName='div'
        noHtml5Validate
        liveValidate
        schema={config.schema}
        uiSchema={getMappingsUiSchema(config)}
        formData={formData}
        onChange={(event: IChangeEvent) => {
          onFormDataChange((event.formData as FlowFormData) ?? {});
          onValidationChangeRef.current?.(event.errors.length === 0);
        }}
        validator={validator}
      >
        <Fragment />
      </RjsfForm>
    </div>
  );
};
